/**
 * v1.14.83.0-fase-7-6 — Admin Pratiche Cyber page.
 *
 * Gemella di PraticheDOPage. Endpoint: /api/admin/cyber/pratiche
 * Slugs download: aig / reale-mutua / lloyds / zip
 * Include badge assuntive_blocks (Reale Mutua) e stato pdf_generated/pending/error.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Download, RefreshCw, Search, FileText, X,
         CheckCircle2, AlertCircle, RotateCw, Archive, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/cyber/pratiche`;

const STATO_BADGE = {
  generated: { label: "Generato", cls: "bg-emerald-100 text-emerald-700",
                icon: CheckCircle2 },
  error: { label: "Errore", cls: "bg-red-100 text-red-700",
            icon: AlertCircle },
  partial: { label: "Parziale", cls: "bg-amber-100 text-amber-700",
              icon: RotateCw },
  pending: { label: "In corso…",
              cls: "bg-slate-100 text-slate-700", icon: Loader2 },
};

async function downloadAdminFile(url, fallbackName) {
  const res = await axios.get(url, { responseType: "blob",
                                       withCredentials: true });
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const name = m ? m[1] : fallbackName;
  const blob = new Blob([res.data], { type: res.headers["content-type"] });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

/** FASE 7-C — Thumbnail preview PDF pagina 1 (150x200) via Blob URL autenticato. */
function CyberThumbnail({ qid, slug, label }) {
  const [src, setSrc] = React.useState(null);
  const [err, setErr] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await axios.get(`${API}/${qid}/thumb/${slug}`, {
          responseType: "blob", withCredentials: true });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qid, slug]);
  if (err) {
    return (
      <div data-testid={`pratica-cyber-thumb-err-${slug}-${qid}`}
            className="w-[75px] h-[100px] border border-[#D9C49A]/40 rounded-sm bg-slate-50 flex items-center justify-center text-[10px] text-slate-400 text-center px-1">
        no preview
      </div>
    );
  }
  if (!src) {
    return (
      <div className="w-[75px] h-[100px] border border-[#D9C49A]/40 rounded-sm bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
      </div>
    );
  }
  return (
    <img src={src} alt={`${label} preview`}
          data-testid={`pratica-cyber-thumb-${slug}-${qid}`}
          className="w-[75px] h-[100px] object-contain border border-[#D9C49A]/40 rounded-sm bg-white shadow-sm" />
  );
}

export default function PraticheCyberPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API, { withCredentials: true });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.status === 401
        ? "Login admin richiesto"
        : "Errore caricamento pratiche Cyber");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (stato && r.pdf_status !== stato) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.ragione_sociale || "").toLowerCase().includes(s)
      || (r.client_email || "").toLowerCase().includes(s)
      || (r.client_name || "").toLowerCase().includes(s);
  });

  const onDownload = async (id, slug) => {
    setBusyId(`${id}:${slug}`);
    try {
      await downloadAdminFile(`${API}/${id}/pdf/${slug}`,
        `Cyber_${slug}_${id.slice(0, 8)}.${slug === "zip" ? "zip" : "pdf"}`);
    } catch (e) {
      toast.error(`Download ${slug} fallito: ${e.response?.data?.detail || e.message}`);
    } finally { setBusyId(null); }
  };

  const onRigenera = async (id) => {
    setBusyId(`${id}:regen`);
    try {
      const { data } = await axios.post(`${API}/${id}/rigenera`, {},
                                          { withCredentials: true });
      toast.success(data.pdf_status === "generated"
        ? "PDF Cyber rigenerati"
        : `Stato: ${data.pdf_status}`);
      await load();
    } catch (e) {
      toast.error("Rigenerazione fallita");
    } finally { setBusyId(null); }
  };

  const onDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/${id}`,
                                         { withCredentials: true });
      setDetail(data);
    } catch (e) { toast.error("Errore dettaglio"); }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFA] p-6" data-testid="pratiche-cyber-page">
      <Toaster richColors position="top-center" />
      <div className="max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="font-heading text-2xl text-[#0F1F14] font-medium">
            Pratiche Cyber
          </h1>
          <p className="text-sm text-[#4A5E51] mt-1">
            Tutti i questionari Cyber Risk compilati dai clienti · Download AIG Cyber, Reale Mutua FUR5166, IPR Lloyd&apos;s e ZIP
          </p>
        </header>

        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5E51]" />
            <input type="text" placeholder="Cerca ragione sociale / email / nome"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="pratiche-cyber-search"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E5E2DB] text-sm outline-none focus:border-[#163321]" />
          </div>
          <select value={stato} onChange={(e) => setStato(e.target.value)}
                  data-testid="pratiche-cyber-filter-stato"
                  className="px-3 py-2 rounded-lg border border-[#E5E2DB] text-sm bg-white">
            <option value="">Tutti gli stati</option>
            <option value="generated">Generato</option>
            <option value="error">Errore</option>
            <option value="partial">Parziale</option>
            <option value="pending">In corso</option>
          </select>
          <button onClick={load} data-testid="pratiche-cyber-reload"
                  className="px-3 py-2 rounded-lg text-sm font-medium border border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E2DB] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F4F0] text-xs text-[#4A5E51] uppercase">
              <tr>
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Ragione Sociale</th>
                <th className="p-3 text-left">Email cliente</th>
                <th className="p-3 text-left">Data</th>
                <th className="p-3 text-left">Stato</th>
                <th className="p-3 text-left">Azioni</th>
              </tr>
            </thead>
            <tbody data-testid="pratiche-cyber-tbody">
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center">
                  <Loader2 className="w-5 h-5 inline animate-spin text-[#163321]" />
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-[#4A5E51]">
                  Nessuna pratica Cyber trovata
                </td></tr>
              )}
              {!loading && filtered.map((r) => {
                const B = STATO_BADGE[r.pdf_status] || STATO_BADGE.pending;
                const Ic = B.icon;
                const isBusy = (slug) => busyId === `${r.id}:${slug}`;
                return (
                  <tr key={r.id} className="border-t border-[#E5E2DB]"
                       data-testid={`pratica-cyber-row-${r.id}`}>
                    <td className="p-3 font-mono text-xs text-[#4A5E51]">
                      {r.id.slice(0, 8)}
                    </td>
                    <td className="p-3 font-medium text-[#0F1F14]">
                      <div>{r.ragione_sociale || "—"}</div>
                      {r.from_do_id && (
                        <div className="text-[10px] text-[#7A8E83] mt-0.5">
                          from D&amp;O: {r.from_do_id.slice(0, 8)}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs text-[#4A5E51]">
                      {r.client_email || "—"}
                    </td>
                    <td className="p-3 text-xs text-[#4A5E51]">
                      {(r.created_at || "—").toString().slice(0, 10)}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${B.cls}`}>
                        <Ic className="w-3 h-3" /> {B.label}
                      </span>
                      {r.has_assuntive_block && (
                        <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-700"
                              data-testid={`pratica-cyber-block-${r.id}`}
                              title="Domande con Stop Assuntivo attivo">
                          <ShieldAlert className="w-2.5 h-2.5" /> Block×{r.assuntive_blocks_count}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {[
                          ["aig", "AIG", FileText],
                          ["reale-mutua", "Reale", FileText],
                          ["lloyds", "Lloyd's", FileText],
                          ["zip", "ZIP", Archive],
                        ].map(([slug, label, Ico]) => (
                          <div key={slug} className="flex flex-col items-center gap-0.5">
                            {slug !== "zip" && (
                              <CyberThumbnail qid={r.id} slug={slug} label={label} />
                            )}
                            <button onClick={() => onDownload(r.id, slug)}
                                    disabled={isBusy(slug)}
                                    data-testid={`pratica-cyber-dl-${slug}-${r.id}`}
                                    className="px-2 py-1 rounded text-xs font-medium bg-[#163321] text-white hover:bg-[#0F2F1F] disabled:opacity-50 inline-flex items-center gap-1">
                              {isBusy(slug)
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Ico className="w-3 h-3" />}
                              {label}
                            </button>
                          </div>
                        ))}
                        <button onClick={() => onDetail(r.id)}
                                data-testid={`pratica-cyber-detail-${r.id}`}
                                className="px-2 py-1 rounded text-xs font-medium border border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5">
                          Dettaglio
                        </button>
                        <button onClick={() => onRigenera(r.id)}
                                disabled={isBusy("regen")}
                                data-testid={`pratica-cyber-regen-${r.id}`}
                                className="px-2 py-1 rounded text-xs font-medium border border-amber-500/40 text-amber-700 hover:bg-amber-500/5 disabled:opacity-50 inline-flex items-center gap-1">
                          {isBusy("regen")
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RotateCw className="w-3 h-3" />}
                          Rigenera
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {detail && <DetailModal data={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function DetailModal({ data, onClose }) {
  const fd = data.form_data || {};
  const blocks = data.assuntive_blocks || [];
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
          onClick={onClose} data-testid="pratica-cyber-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-[#0F1F14] font-medium">
            Pratica Cyber {data.id?.slice(0, 8)}
          </h2>
          <button onClick={onClose} data-testid="pratica-cyber-detail-close"
                  className="p-1 rounded hover:bg-[#F5F4F0]">
            <X className="w-5 h-5 text-[#4A5E51]" />
          </button>
        </div>
        <div className="mb-3 text-xs text-[#4A5E51]">
          Status: <strong>{data.status}</strong> ·
          PDF: <strong>{data.pdf_status}</strong> ·
          Schema: <strong>{data.schema_version}</strong> ·
          Source: <strong>{data.source}</strong>
        </div>
        {blocks.length > 0 && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded"
                data-testid="pratica-cyber-blocks">
            <p className="text-xs font-semibold text-orange-800 mb-1.5 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              Stop Assuntivo — {blocks.length} domand{blocks.length === 1 ? "a scattata" : "e scattate"}
            </p>
            <ul className="text-[11px] text-orange-700 space-y-0.5 ml-5 list-disc">
              {blocks.map((b, i) => (
                <li key={i}>
                  <span className="font-mono">{b.field}</span> — {b.label}
                  {b.pdf_ref && <span className="text-orange-500"> [{b.pdf_ref}]</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.pdf_error_log && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            Errore: {data.pdf_error_log}
          </div>
        )}
        {Object.entries(fd).map(([sez, fields]) => (
          <details key={sez} open
                    className="mb-3 border border-[#E5E2DB] rounded-xl overflow-hidden">
            <summary className="cursor-pointer p-3 font-semibold bg-[#F5F4F0] text-[#163321] capitalize">
              {sez.replace(/_/g, " ")}
            </summary>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {Object.entries(fields || {}).map(([k, v]) => (
                <div key={k} className="border-b border-[#E5E2DB] py-1">
                  <span className="text-[#4A5E51] mr-2">{k}:</span>
                  <span className="text-[#0F1F14] font-medium break-all">
                    {typeof v === "boolean" ? (v ? "Sì" : "No")
                     : typeof v === "object" ? JSON.stringify(v)
                     : String(v ?? "—")}
                  </span>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
